import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AnchorLink,
  Button,
  Container,
  Icon,
  IconList,
  Layout,
  Padding,
  Text,
} from "@kidslivesafe/sc-react";
import {
  useAppDispatch,
  useAppSelector,
  useHandlePageNavigate,
} from "../../../hooks/hooks";
import ProtectionShell from "./ProtectionShell";
import StatusPill from "./StatusPill";
import ProfileCard from "./ProfileCard";
import { selectProtectionState } from "../../../features/profileProtection/profileProtectionSlice";
import { sendMemberEventAsync } from "../../../features/admin/adminSlice";

type ExposureSite = {
  id: string;
  name: string;
  status: "removed" | "likely_exposure";
};

const ScanExposures: React.FC = () => {
  const dispatch = useAppDispatch();
  const handleNavigate = useHandlePageNavigate();
  const { profileName, profileLocation, defaultBrokerSites } = useAppSelector(
    selectProtectionState,
  );
  const sites = useMemo<ExposureSite[]>(
    () =>
      defaultBrokerSites.map(site => ({
        id: site.brandName.toLowerCase().replace(/\s+/g, "-"),
        name: site.brandName,
        status:
          site.statusLabel?.toLowerCase() === "done"
            ? "removed"
            : "likely_exposure",
      })),
    [defaultBrokerSites],
  );
  const [visibleCount, setVisibleCount] = useState<number>(0);
  const [isComplete, setIsComplete] = useState<boolean>(false);
  const [expandedSites, setExpandedSites] = useState<Set<string>>(new Set());
  const hasSentScanViewedEvent = useRef(false);
  const hasSentScanContinuedEvent = useRef(false);

  const toggleSite = (id: string) => {
    setExpandedSites(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const revealIntervalMs = 1000;

  useEffect(() => {
    if (!hasSentScanViewedEvent.current) {
      dispatch(
        sendMemberEventAsync({
          EventCategory: "Profile Protection",
          EventAction: "Scan Viewed",
        }),
      );
      hasSentScanViewedEvent.current = true;
    }
  }, [dispatch]);

  useEffect(() => {
    if (sites.length === 0) {
      setVisibleCount(0);
      setIsComplete(true);
      return;
    }

    setVisibleCount(0);
    setIsComplete(false);
    setExpandedSites(new Set());
  }, [sites.length]);

  useEffect(() => {
    if (sites.length === 0 || isComplete) {
      return;
    }

    const timer = setInterval(() => {
      setVisibleCount(current => {
        if (current + 1 >= sites.length) {
          clearInterval(timer);
          setIsComplete(true);
          return sites.length;
        }

        return current + 1;
      });
    }, revealIntervalMs);

    return () => clearInterval(timer);
  }, [sites.length, isComplete, revealIntervalMs]);

  const visibleSites = useMemo(() => {
    return sites.slice(0, visibleCount);
  }, [sites, visibleCount]);

  const showScanStatusCard = sites.length > 0;

  return (
    <ProtectionShell
      title={isComplete ? "Your likely exposures" : "Scanning for exposures..."}
      subtitle="We found people-search sources that may have your information listed."
      subtitleColor="dark-gray"
      stepLabel="Step 1 of 3"
    >
      <Layout type="flex" flexProps={{ direction: "column", gap: "md" }}>
        <Padding top="lg" bottom="md">
          <ProfileCard
            name={profileName || "Protected Profile"}
            location={profileLocation}
            showStatus={false}
          />
        </Padding>
        <Layout type="flex" flexProps={{ direction: "column" }}>
          <Container
            border
            borderThickness={1}
            noBottomBorder
            borderRadiusLocations={{ topLeft: true, topRight: true }}
          >
            <Padding space="sm">
              <Layout
                type="flex"
                flexProps={{
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text fontWeight="semibold">Likely Exposures</Text>
                <Layout
                  type="flex"
                  flexProps={{ gap: "xxs", alignItems: "center" }}
                >
                  <Icon name={IconList.IconSearch} size={14} />
                  <Text size="xs" color="dark-gray">
                    Scanned{" "}
                    {new Date().toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </Text>
                </Layout>
              </Layout>
            </Padding>
          </Container>
          {visibleSites.map(site => (
            <div
              key={site.id}
              className="profile-protection__fade-in profile-protection__site-row"
            >
              <Container border borderThickness={1} noTopBorder>
                <Padding space="md">
                  <Layout
                    type="flex"
                    flexProps={{ direction: "column", gap: "xs" }}
                  >
                    <Layout
                      type="flex"
                      flexProps={{
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Layout
                        type="flex"
                        flexProps={{ gap: "xs", alignItems: "center" }}
                      >
                        <div onClick={() => toggleSite(site.id)}>
                          <Icon
                            name={IconList.IconCaret}
                            size={14}
                            className={`profile-protection__caret ${
                              expandedSites.has(site.id)
                                ? "profile-protection__caret--open"
                                : ""
                            }`}
                          />
                        </div>

                        <Text fontWeight="semibold">{site.name}</Text>
                      </Layout>
                      <StatusPill status={site.status} />
                    </Layout>
                    {expandedSites.has(site.id) ? (
                      <Text size="xs" color="dark-gray">
                        This source likely has a listing matching your profile.
                      </Text>
                    ) : null}
                  </Layout>
                </Padding>
              </Container>
            </div>
          ))}
          {showScanStatusCard ? (
            <Container border noTopBorder borderThickness={1}>
              <Padding space="md">
                <Layout
                  type="flex"
                  flexProps={{
                    justifyContent: "flex-start",
                    alignItems: "center",
                    gap: "lg",
                  }}
                >
                  {isComplete ? (
                    <Text color="success" tag="span">
                      <Icon name={IconList.IconCheck} size={14} />
                    </Text>
                  ) : (
                    <div className="profile-protection__spinner" />
                  )}

                  <Text size="xs" color={isComplete ? "dark-gray" : undefined}>
                    {isComplete
                      ? `Scan complete -- ${sites.length} likely exposures found`
                      : "Scanning for possible exposure"}
                  </Text>
                </Layout>
              </Padding>
            </Container>
          ) : null}
        </Layout>
      </Layout>

      {isComplete && (
        <Padding top="md">
          <Container border bodyBorderRadius>
            <Padding space="md">
              <Layout
                type="flex"
                flexProps={{
                  gap: "sm",
                  alignItems: "flex-start",
                  direction: "row",
                }}
              >
                <Icon name={IconList.IconHelp} size={20} />
                <Text size="sm" color="dark-gray">
                  Next, we&apos;ll confirm your details and submit removal
                  requests to these sources on your behalf. Most sources process
                  requests within a few days.
                </Text>
              </Layout>
            </Padding>
          </Container>
        </Padding>
      )}

      {isComplete && (
        <Padding top="md" bottom="md">
          <Layout type="flex" flexProps={{ direction: "column", gap: "md" }}>
            <Button
              buttonText="Continue - start removal process"
              fill="fill"
              color="primary"
              handleClick={() => {
                if (!hasSentScanContinuedEvent.current) {
                  dispatch(
                    sendMemberEventAsync({
                      EventCategory: "Profile Protection",
                      EventAction: "Scan Continued",
                    }),
                  );
                  hasSentScanContinuedEvent.current = true;
                }
                handleNavigate("/protection/info");
              }}
            />
            <Layout type="flex" flexProps={{ justifyContent: "center" }}>
              <AnchorLink role="button" handleClick={() => handleNavigate("/")}>
                Back to dashboard
              </AnchorLink>
            </Layout>
          </Layout>
        </Padding>
      )}
    </ProtectionShell>
  );
};

export default ScanExposures;
